import { TFunction } from 'i18next';

import {
  ENTITY_TYPE,
  ENTITY_TYPE_LABEL,
  type TEntityType,
} from './dependencyGraph';

// The lines a creation's status shows: the customer service's for every new customer
// (DomainCreateProcessService.getMessages), then domain-copy's for a copy (DomainCopyOrchestrator).
// Warnings and failures carry names and other services' errors, so they are shown as written.
const CREATION_SUCCESS_MESSAGE = 'customer successfully created';
const FIXED_LINE_LABEL = new Map([
  ['request received', 'customer.progressRequestReceived'],
  [CREATION_SUCCESS_MESSAGE, 'customer.progressCustomerCreated'],
  ['Copied customer settings', 'copyTenant.progressSettingsCopied'],
  ['Copy completed successfully', 'copyTenant.progressCompleted'],
]);
const STEP_STATUS = /^(Domain|District|Unit) status: (.+)$/;
const STEP_STATUS_LABEL = new Map([
  ['Domain', 'customer.progressDomainStatus'],
  ['District', 'customer.progressDistrictStatus'],
  ['Unit', 'customer.progressUnitStatus'],
]);
// A step's status is written once the step has started, so never "not started yet".
const STATUS_LABEL = new Map([
  ['in progress', 'deletionStatus.inProgress'],
  ['done', 'deletionStatus.done'],
  ['failed', 'deletionStatus.failed'],
]);
const COPIED = /^Copied ([A-Z_]+)$/;
const STARTING_LAYER = /^Starting layer (\d+): \[(.+)\]$/;

const isEntityType = (value: string): value is TEntityType =>
  Object.values(ENTITY_TYPE).some((type) => type === value);

// A creation progress line in the user's language. A line it doesn't know is returned as written.
const translateProgressMessage = (message: string, t: TFunction) => {
  // A type this page doesn't know yet keeps its name as domain-copy wrote it.
  const typeName = (type: string) =>
    isEntityType(type) ? t(ENTITY_TYPE_LABEL[type]) : type;

  const fixedLine = FIXED_LINE_LABEL.get(message);
  if (fixedLine) {
    return t(fixedLine);
  }
  const step = STEP_STATUS.exec(message);
  const stepLabel = step && STEP_STATUS_LABEL.get(step[1]);
  const statusLabel = step && STATUS_LABEL.get(step[2]);
  if (stepLabel && statusLabel) {
    return t(stepLabel, { status: t(statusLabel) });
  }
  const copied = COPIED.exec(message);
  if (copied) {
    return t('copyTenant.progressCopied', { type: typeName(copied[1]) });
  }
  const layer = STARTING_LAYER.exec(message);
  if (layer) {
    return t('copyTenant.progressStartingLayer', {
      layer: layer[1],
      types: layer[2].split(', ').map(typeName).join(', '),
    });
  }
  return message;
};

export { CREATION_SUCCESS_MESSAGE, translateProgressMessage };
